import { create } from "zustand";

import type {
  ActionSourceCandidate,
  ActionSourceConfirmationPendingAction,
} from "@ando/core/ws";

/**
 * sourceConfirmationStore — pending "which file did you mean?" cards.
 *
 * Mirrors the ``activeActionStore`` shape (Zustand record + insertion
 * order + selectors) but stays in its own slice because:
 *
 *   * Lifecycle is different — confirmations are ephemeral (10-min
 *     Redis TTL server-side) and SHOULD be dismissed once the user
 *     picks a candidate; action proposals persist across the
 *     conversation.
 *   * Semantics is different — a confirmation is a disambiguation
 *     prompt, not a preview-and-approve action. Keeping the stores
 *     separate avoids overloading ``ActiveAction.status`` with a 5th
 *     phase that only applies to one subtype.
 *
 * Pattern: BE emits ``action_source_confirmation`` WS event → bridge
 * upserts here → workspace-aware surface mounts the
 * <SourceConfirmationCard>. Confirming POSTs to
 * ``/api/v1/actions/confirm-source/{id}`` which triggers the regular
 * ``action_proposal`` event — at that point the confirmation row is
 * removed and ``activeActionStore`` takes over.
 *
 * Per ``ando_ui_v2_rewrite_plan.md`` §A.7: work-area surfaces own
 * agentic artefacts, chat stays conversation-only.
 */

export type PendingConfirmationStatus =
  /** Awaiting user pick. */
  | "pending"
  /** POST in flight after the user clicked "Conferma". */
  | "confirming"
  /** BE returned proposal_created — card fades out briefly. */
  | "confirmed"
  /** Source picked + BE acked ``executing``: the Temporal fork-edit is
   * authoring the document (30-60s). The card stays mounted as a "Genero la
   * versione…" loader (NOT removed) so the limbo between pick and the redline
   * proposal never looks dead; cleared when ``action_proposal`` arrives (or a
   * safety timeout). Drives the work-area loader in ``ArtifactPane`` too. */
  | "executing"
  /** POST failed; banner with retry. */
  | "error";

export interface PendingConfirmation {
  /** Opaque BE-issued id (URL-safe; lives ≤10 min). */
  id: string;
  /** Originating conversation — used by the surface for ACL/scoping. */
  conversationId: string;
  /** Originating query — surfaces a back-reference chip to the chat. */
  queryId: string;
  /** Candidate set the user picks from (always ≥1 in practice). */
  candidates: readonly ActionSourceCandidate[];
  /** BE pre-pick — store seeds ``selectedContentItemId`` with this. */
  preferredContentItemId: string;
  /** What the agent intends to do once the source is confirmed. */
  pendingAction: ActionSourceConfirmationPendingAction;
  /** Local user pick (defaults to ``preferredContentItemId``). */
  selectedContentItemId: string;
  /** State machine. */
  status: PendingConfirmationStatus;
  /** Populated when ``status === "error"``. */
  errorMessage?: string | null;
  /** Wall-clock arrival (ISO) — used for sort order in the surface. */
  receivedAt: string;
}

interface SourceConfirmationState {
  confirmations: Record<string, PendingConfirmation>;
  /** Insertion order so the surface renders oldest-first deterministically. */
  order: string[];
  upsert: (confirmation: PendingConfirmation) => void;
  patch: (id: string, patch: Partial<PendingConfirmation>) => void;
  remove: (id: string) => void;
  clear: () => void;
}

export const useSourceConfirmationStore = create<SourceConfirmationState>()(
  (set) => ({
    confirmations: {},
    order: [],
    upsert: (confirmation) =>
      set((state) => {
        const exists = state.confirmations[confirmation.id] !== undefined;
        return {
          confirmations: {
            ...state.confirmations,
            [confirmation.id]: confirmation,
          },
          order: exists ? state.order : [...state.order, confirmation.id],
        };
      }),
    patch: (id, patch) =>
      set((state) => {
        const cur = state.confirmations[id];
        if (!cur) return state;
        return {
          confirmations: {
            ...state.confirmations,
            [id]: { ...cur, ...patch },
          },
        };
      }),
    remove: (id) =>
      set((state) => {
        if (!state.confirmations[id]) return state;
        const next = { ...state.confirmations };
        delete next[id];
        return {
          confirmations: next,
          order: state.order.filter((x) => x !== id),
        };
      }),
    clear: () => set({ confirmations: {}, order: [] }),
  }),
);

// ── Selectors ────────────────────────────────────────────────────────────

export const selectConfirmations = (
  state: SourceConfirmationState,
): readonly PendingConfirmation[] => {
  const out: PendingConfirmation[] = [];
  for (const id of state.order) {
    const c = state.confirmations[id];
    if (c) out.push(c);
  }
  return out;
};

/** True while ANY confirmation is mid-generation (``status === "executing"``)
 * — the source was picked and the BE is authoring the fork-edit. The
 * work-area (``ArtifactPane``) keys its "Genero la versione modificata…"
 * loader off this so the post-pick limbo never renders the bare "Documento
 * non disponibile" state. */
export const selectHasExecutingFork = (
  state: SourceConfirmationState,
): boolean => {
  for (const id of state.order) {
    if (state.confirmations[id]?.status === "executing") return true;
  }
  return false;
};

/** All confirmations whose ``pendingAction.action_type`` resolves to the
 * given workspace tab. Today every confirmation targets
 * ``document_fork_edit`` → "documents" tab, but routing the filter
 * through the same ``actionTypeToTab`` map future-proofs the surface
 * for new propose_* tools joining the confirmation pattern. */
export const selectConfirmationsByActionType =
  (actionTypes: readonly string[]) =>
  (state: SourceConfirmationState): readonly PendingConfirmation[] => {
    if (state.order.length === 0) return [];
    const out: PendingConfirmation[] = [];
    for (const id of state.order) {
      const c = state.confirmations[id];
      if (c && actionTypes.includes(c.pendingAction.action_type)) {
        out.push(c);
      }
    }
    return out;
  };
